import { createInstance, dateReviver } from '@/api';
import { ProductChangeHistoryClient, ProductChangeHistory, ProductChangeHistoryEntity } from '@/api/frontoffice/clients';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { i18n } from '@/i18n';

/** The on demand loaded change history of a single entity. */
export interface EntityChangeHistory {
    loading: boolean;
    /** Set when the figures could not be calculated, so the entity can show an alert instead. */
    failed: boolean;
    changeHistory: ProductChangeHistory;
}

const notLoaded: EntityChangeHistory = { loading: false, failed: false, changeHistory: null };

export interface ProductChangeHistoryStore {
    loadingEntities: boolean;
    entities: ProductChangeHistoryEntity[];
    changeHistoryByEntityId: Record<number, EntityChangeHistory>;
}

export const client = new ProductChangeHistoryClient('', createInstance(), dateReviver);

export const useProductChangeHistoryStore = defineStore('products-product-change-history-store', {
    state: (): ProductChangeHistoryStore => ({
        loadingEntities: false,
        entities: null,
        changeHistoryByEntityId: {}
    }),
    getters: {
        entityChangeHistory: (state) => {
            return (entityId: number): EntityChangeHistory => state.changeHistoryByEntityId[entityId] ?? notLoaded;
        }
    },
    actions: {
        // The entity list only hits the publication database, so it loads fast.
        async getEntities(runId: string): Promise<void> {
            this.loadingEntities = true;
            this.entities = null;
            this.changeHistoryByEntityId = {};
            useAlertStore().clearAlert();
            try {
                this.entities = await client.getProductChangeHistoryEntities(runId);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('productChangeHistory.detail.error.title'), error);
            } finally {
                this.loadingEntities = false;
            }
        },
        // The figures of a single entity are expensive to calculate, so they are requested
        // on demand when the accordion of that entity is opened.
        async loadChangeHistory(runId: string, entityId: number): Promise<void> {
            const loaded = this.entityChangeHistory(entityId);

            // Requested at most once per entity: reopening an accordion reuses the result, and a
            // failed attempt stays failed until the page is refreshed rather than retrying the
            // expensive query on every open.
            if (loaded.loading || loaded.changeHistory || loaded.failed) {
                return;
            }

            this.changeHistoryByEntityId[entityId] = { loading: true, failed: false, changeHistory: null };
            try {
                const changeHistory = await client.getProductChangeHistory(runId, entityId);
                this.changeHistoryByEntityId[entityId] = { loading: false, failed: false, changeHistory };
            } catch {
                // No global alert: the entity itself shows the failure in place of its figures.
                this.changeHistoryByEntityId[entityId] = { loading: false, failed: true, changeHistory: null };
            }
        },
        clear() {
            this.loadingEntities = false;
            this.entities = null;
            this.changeHistoryByEntityId = {};
            useAlertStore().clearAlert();
        }
    }
});
